import React from 'react';
import {
  Box,
  VStack,
  HStack,
  Text,
  Heading,
  Badge,
  Image,
  Code,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  TableContainer,
  Progress,
  Flex,
  Circle,
  Wrap,
  WrapItem,
  SimpleGrid,
} from '@chakra-ui/react';
import { CheckIcon, CloseIcon, WarningIcon, MinusIcon } from '@chakra-ui/icons';

// Status indicator with icon
function StatusIndicator({ status }) {
  const configs = {
    pass: { icon: CheckIcon, bg: 'green.500', color: 'white', label: 'Pass' },
    fail: { icon: CloseIcon, bg: 'red.500', color: 'white', label: 'Fail' },
    warning: { icon: WarningIcon, bg: 'yellow.500', color: 'gray.900', label: 'Warning' },
    not_found: { icon: MinusIcon, bg: 'gray.600', color: 'gray.400', label: 'Not Found' },
    error: { icon: CloseIcon, bg: 'red.500', color: 'white', label: 'Error' },
  };

  const config = configs[status] || configs.not_found;
  const IconComponent = config.icon;

  return (
    <HStack spacing={2}>
      <Circle size="24px" bg={config.bg}>
        <IconComponent color={config.color} boxSize={3} />
      </Circle>
      <Text
        fontSize="sm"
        fontWeight="semibold"
        color={config.bg}
        textTransform="uppercase"
      >
        {config.label}
      </Text>
    </HStack>
  );
}

// PageSpeed circular gauge
function PageSpeedGauge({ score, label }) {
  const getColor = (s) => {
    if (s === null || s === undefined) return { main: 'gray.500', bg: 'gray.700' };
    if (s >= 90) return { main: 'green.400', bg: 'green.900' };
    if (s >= 50) return { main: 'yellow.400', bg: 'yellow.900' };
    return { main: 'red.400', bg: 'red.900' };
  };

  const colors = getColor(score);
  const displayScore = score !== null && score !== undefined ? score : '--';

  return (
    <VStack spacing={2}>
      <Box
        position="relative"
        w="100px"
        h="100px"
        borderRadius="full"
        bg={colors.bg}
        display="flex"
        alignItems="center"
        justifyContent="center"
        borderWidth="4px"
        borderColor={colors.main}
      >
        <Text color={colors.main} fontSize="2xl" fontWeight="bold">
          {displayScore}
        </Text>
      </Box>
      <Text color="gray.400" fontSize="sm" fontWeight="medium">
        {label}
      </Text>
    </VStack>
  );
}

// Metric card for individual performance metrics
function MetricCard({ label, value, score }) {
  const getColor = (s) => {
    if (s === null || s === undefined) return 'gray.400';
    if (s >= 0.9) return 'green.400';
    if (s >= 0.5) return 'yellow.400';
    return 'red.400';
  };

  const color = getColor(score);

  return (
    <VStack
      bg="whiteAlpha.50"
      borderRadius="lg"
      px={4}
      py={3}
      spacing={1}
      minW="100px"
      align="center"
    >
      <Text color="gray.500" fontSize="sm" fontWeight="medium" textTransform="uppercase">
        {label}
      </Text>
      <Text color={color} fontSize="sm" fontWeight="bold">
        {value || '--'}
      </Text>
    </VStack>
  );
}

// Category table component
function CategoryTable({ title, icon, colorScheme, checks }) {
  // Monochromatic gradient colors for each scheme
  const gradients = {
    purple: {
      header: 'linear(135deg, #1a1225 0%, #2d1f4a 50%, #1a1225 100%)',
      headerRow: 'linear(to-r, #16101f, #1f1832, #16101f)',
      border: '#3d2a5c',
      iconBg: 'linear(135deg, #3d2a5c, #5a3d8a)',
    },
    pink: {
      header: 'linear(135deg, #25121a 0%, #4a1f3a 50%, #25121a 100%)',
      headerRow: 'linear(to-r, #1f1018, #321828, #1f1018)',
      border: '#5c2a4a',
      iconBg: 'linear(135deg, #5c2a4a, #8a3d6d)',
    },
    blue: {
      header: 'linear(135deg, #121825 0%, #1f2d4a 50%, #121825 100%)',
      headerRow: 'linear(to-r, #10151f, #182032, #10151f)',
      border: '#2a3d5c',
      iconBg: 'linear(135deg, #2a3d5c, #3d5a8a)',
    },
    orange: {
      header: 'linear(135deg, #251a12 0%, #4a351f 50%, #251a12 100%)',
      headerRow: 'linear(to-r, #1f1610, #322418, #1f1610)',
      border: '#5c4a2a',
      iconBg: 'linear(135deg, #5c4a2a, #8a6d3d)',
    },
  };

  const colors = gradients[colorScheme] || gradients.blue;

  return (
    <Box
      bg="#141922"
      borderRadius="xl"
      overflow="hidden"
      borderWidth="1px"
      borderColor="gray.800"
      maxW="1200px"
      mx="auto"
      w="100%"
    >
      {/* Category Header - monochromatic gradient */}
      <HStack
        bgGradient={colors.header}
        borderBottomWidth="1px"
        borderBottomColor={colors.border}
        px={6}
        py={5}
        spacing={4}
      >
        <Circle size="40px" bgGradient={colors.iconBg} boxShadow="lg">
          <Text fontSize="lg">{icon}</Text>
        </Circle>
        <Text
          color="gray.100"
          fontWeight="bold"
          fontSize="lg"
          letterSpacing="wider"
          textTransform="uppercase"
        >
          {title}
        </Text>
      </HStack>

      {/* Table */}
      <TableContainer>
        <Table variant="simple" size="sm" sx={{ tableLayout: 'fixed' }}>
          <Thead>
            <Tr bgGradient={colors.headerRow}>
              <Th color="gray.500" borderColor="gray.800" py={3} fontSize="sm" w="200px" fontWeight="medium" textTransform="uppercase" letterSpacing="wider">Check</Th>
              <Th color="gray.500" borderColor="gray.800" py={3} fontSize="sm" w="140px" fontWeight="medium" textTransform="uppercase" letterSpacing="wider">Status</Th>
              <Th color="gray.500" borderColor="gray.800" py={3} fontSize="sm" fontWeight="medium" textTransform="uppercase" letterSpacing="wider">Details</Th>
            </Tr>
          </Thead>
          <Tbody>
            {checks.map((check, index) => (
              <Tr
                key={index}
                _hover={{ bg: 'whiteAlpha.50' }}
                transition="background 0.15s"
              >
                <Td borderColor="gray.800" py={4}>
                  <Text color="white" fontWeight="medium" fontSize="sm">
                    {check.label}
                  </Text>
                </Td>
                <Td borderColor="gray.800" py={4}>
                  <StatusIndicator status={check.data.status} />
                </Td>
                <Td borderColor="gray.800" py={4} maxW="400px">
                  <VStack align="start" spacing={1}>
                    <Text color="gray.400" fontSize="sm">
                      {check.data.message}
                    </Text>
                    {check.detail}
                  </VStack>
                </Td>
              </Tr>
            ))}
          </Tbody>
        </Table>
      </TableContainer>
    </Box>
  );
}

function ChecklistResults({ results }) {
  const { checks, summary, url } = results;

  // Calculate pass rate
  const passRate = Math.round((summary.passed / summary.total) * 100);

  // Organize checks by category
  const seoChecks = [
    {
      label: 'H1 Tag',
      data: checks.h1Tag,
      detail: checks.h1Tag.content && (
        <Code fontSize="sm" bg="whiteAlpha.100" color="gray.300" px={2} py={1} borderRadius="md" display="block" whiteSpace="normal" wordBreak="break-word">
          {checks.h1Tag.content}
        </Code>
      ),
    },
    {
      label: 'Favicon',
      data: checks.favicon,
      detail: checks.favicon.found && checks.favicon.url && (
        <HStack spacing={2}>
          <Image
            src={checks.favicon.url.startsWith('http') ? checks.favicon.url : `${url}${checks.favicon.url.startsWith('/') ? '' : '/'}${checks.favicon.url}`}
            alt="Favicon"
            boxSize="16px"
            borderRadius="sm"
            fallbackSrc="data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 16 16'%3E%3Crect fill='%23666' width='16' height='16'/%3E%3C/svg%3E"
          />
        </HStack>
      ),
    },
    {
      label: 'Meta Title',
      data: checks.metaTitle,
      detail: checks.metaTitle.content && (
        <Code fontSize="sm" bg="whiteAlpha.100" color="gray.300" px={2} py={1} borderRadius="md" display="block" whiteSpace="normal" wordBreak="break-word">
          {checks.metaTitle.content}
        </Code>
      ),
    },
    {
      label: 'Meta Description',
      data: checks.metaDescription,
      detail: checks.metaDescription.content && (
        <Code fontSize="sm" bg="whiteAlpha.100" color="gray.300" px={2} py={1} borderRadius="md" display="block" whiteSpace="normal" wordBreak="break-word">
          {checks.metaDescription.content}
        </Code>
      ),
    },
  ];

  const trackingChecks = [
    {
      label: 'Google Tag Manager',
      data: checks.gtm,
      detail: checks.gtm.containerId && (
        <Badge colorScheme="purple" fontSize="sm">{checks.gtm.containerId}</Badge>
      ),
    },
    {
      label: 'Microsoft Clarity',
      data: checks.microsoftClarity,
      detail: null,
    },
    {
      label: 'Call Tracking',
      data: checks.callTracking,
      detail: checks.callTracking.phoneNumbers && checks.callTracking.phoneNumbers.length > 0 && (
        <Wrap spacing={2}>
          {checks.callTracking.phoneNumbers.map((phone, index) => (
            <WrapItem key={index}>
              <Badge colorScheme="purple" fontSize="sm">
                {phone}
              </Badge>
            </WrapItem>
          ))}
        </Wrap>
      ),
    },
    {
      label: 'Searchlight',
      data: checks.searchlight,
      detail: null,
    },
    // Only include pixels row if pixels were found
    ...(checks.pixels.found ? [{
      label: 'Tracking Pixels',
      data: checks.pixels,
      detail: (
        <Wrap spacing={2}>
          {checks.pixels.pixels.map((pixel, index) => (
            <WrapItem key={index}>
              <Badge colorScheme="purple" fontSize="sm">
                {pixel.name}{pixel.id ? `: ${pixel.id}` : ''}
              </Badge>
            </WrapItem>
          ))}
        </Wrap>
      ),
    }] : []),
  ];

  const integrationChecks = [
    {
      label: 'Userway (Accessibility)',
      data: checks.userway,
      detail: null,
    },
    {
      label: 'Scheduler',
      data: checks.scheduler,
      detail: checks.scheduler.found && checks.scheduler.provider && (
        <Badge colorScheme="blue" fontSize="sm">{checks.scheduler.provider}</Badge>
      ),
    },
    {
      label: 'Chat Widget',
      data: checks.chatWidget,
      detail: checks.chatWidget?.found && checks.chatWidget?.providers?.length > 0 && (
        <Wrap spacing={2}>
          {checks.chatWidget.providers.map((provider, index) => (
            <WrapItem key={index}>
              <Badge colorScheme="blue" fontSize="sm">{provider}</Badge>
            </WrapItem>
          ))}
        </Wrap>
      ),
    },
    {
      label: 'Cleantalk (Spam Protection)',
      data: checks.cleantalk,
      detail: null,
    },
    {
      label: 'Mailgun DNS',
      data: checks.mailgun,
      detail: checks.mailgun.mxRecord && (
        <Code fontSize="sm" bg="whiteAlpha.100" color="gray.400" px={2} py={1} borderRadius="md">
          {checks.mailgun.mxRecord}
        </Code>
      ),
    },
  ];

  return (
    <VStack align="stretch" spacing={10}>
      {/* Summary Header */}
      <Box
        bg="linear-gradient(135deg, #1A1F2E 0%, #141922 100%)"
        borderRadius="xl"
        borderWidth="1px"
        borderColor="gray.700"
        overflow="hidden"
        maxW="1200px"
        mx="auto"
        w="100%"
      >
        {/* Top section with URL and stats */}
        <Box p={6}>
          <Flex justify="space-between" align="start" flexWrap="wrap" gap={4}>
            <VStack align="start" spacing={2}>
              <HStack spacing={2}>
                <Circle size="8px" bg="green.400" />
                <Text color="gray.500" fontSize="sm" fontWeight="medium" textTransform="uppercase" letterSpacing="wider">
                  Scan Complete
                </Text>
              </HStack>
              <Heading size="lg" color="white">Launch Checklist Results</Heading>
              <Code
                fontSize="sm"
                bg="whiteAlpha.100"
                color="purple.300"
                px={3}
                py={1}
                borderRadius="md"
              >
                {url}
              </Code>
            </VStack>

            {/* Stats */}
            <HStack spacing={4}>
              <VStack
                bg="green.900"
                borderWidth="1px"
                borderColor="green.700"
                borderRadius="lg"
                px={5}
                py={3}
                minW="80px"
              >
                <Text color="green.300" fontSize="2xl" fontWeight="bold" lineHeight="1">
                  {summary.passed}
                </Text>
                <Text color="gray.400" fontSize="sm">Passed</Text>
              </VStack>

              {summary.warnings > 0 && (
                <VStack
                  bg="yellow.900"
                  borderWidth="1px"
                  borderColor="yellow.700"
                  borderRadius="lg"
                  px={5}
                  py={3}
                  minW="80px"
                >
                  <Text color="yellow.300" fontSize="2xl" fontWeight="bold" lineHeight="1">
                    {summary.warnings}
                  </Text>
                  <Text color="gray.400" fontSize="sm">Warnings</Text>
                </VStack>
              )}

              {summary.failed > 0 && (
                <VStack
                  bg="red.900"
                  borderWidth="1px"
                  borderColor="red.700"
                  borderRadius="lg"
                  px={5}
                  py={3}
                  minW="80px"
                >
                  <Text color="red.300" fontSize="2xl" fontWeight="bold" lineHeight="1">
                    {summary.failed}
                  </Text>
                  <Text color="gray.400" fontSize="sm">Failed</Text>
                </VStack>
              )}
            </HStack>
          </Flex>
        </Box>

        {/* Progress bar */}
        <Box px={6} pb={4}>
          <HStack justify="space-between" mb={2}>
            <Text color="gray.500" fontSize="sm">Overall Pass Rate</Text>
            <Text color={passRate >= 80 ? 'green.400' : passRate >= 50 ? 'yellow.400' : 'red.400'} fontSize="sm" fontWeight="bold">
              {passRate}%
            </Text>
          </HStack>
          <Progress
            value={passRate}
            size="sm"
            colorScheme={passRate >= 80 ? 'green' : passRate >= 50 ? 'yellow' : 'red'}
            bg="gray.700"
            borderRadius="full"
          />
        </Box>
      </Box>

      {/* PageSpeed Section */}
      {checks.pagespeed && (
        <Box
          bg="#141922"
          borderRadius="xl"
          borderWidth="1px"
          borderColor="gray.800"
          overflow="hidden"
          maxW="1200px"
          mx="auto"
          w="100%"
        >
          <HStack
            bgGradient="linear(135deg, #251a12 0%, #4a351f 50%, #251a12 100%)"
            borderBottomWidth="1px"
            borderBottomColor="#5c4a2a"
            px={6}
            py={5}
            spacing={4}
          >
            <Circle size="40px" bgGradient="linear(135deg, #5c4a2a, #8a6d3d)" boxShadow="lg">
              <Text fontSize="lg">⚡</Text>
            </Circle>
            <Text
              color="gray.100"
              fontWeight="bold"
              fontSize="lg"
              letterSpacing="wider"
              textTransform="uppercase"
            >
              PERFORMANCE
            </Text>
          </HStack>

          <Box p={6}>
            <VStack spacing={4}>
              <Text color="gray.400" fontSize="sm">Google PageSpeed Insights</Text>

              <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4} w="100%">
                {/* Desktop */}
                <HStack spacing={4} align="center" justify="flex-start">
                  <PageSpeedGauge score={checks.pagespeed.desktop?.score} label="Desktop" />
                  {checks.pagespeed.desktop?.metrics && (
                    <SimpleGrid columns={3} spacing={2} flex={1}>
                      <MetricCard label="FCP" value={checks.pagespeed.desktop.metrics.fcp?.displayValue} score={checks.pagespeed.desktop.metrics.fcp?.score} />
                      <MetricCard label="LCP" value={checks.pagespeed.desktop.metrics.lcp?.displayValue} score={checks.pagespeed.desktop.metrics.lcp?.score} />
                      <MetricCard label="TBT" value={checks.pagespeed.desktop.metrics.tbt?.displayValue} score={checks.pagespeed.desktop.metrics.tbt?.score} />
                      <MetricCard label="CLS" value={checks.pagespeed.desktop.metrics.cls?.displayValue} score={checks.pagespeed.desktop.metrics.cls?.score} />
                      <MetricCard label="SI" value={checks.pagespeed.desktop.metrics.speedIndex?.displayValue} score={checks.pagespeed.desktop.metrics.speedIndex?.score} />
                    </SimpleGrid>
                  )}
                </HStack>

                {/* Mobile */}
                <HStack spacing={4} align="center" justify="flex-start">
                  <PageSpeedGauge score={checks.pagespeed.mobile?.score} label="Mobile" />
                  {checks.pagespeed.mobile?.metrics && (
                    <SimpleGrid columns={3} spacing={2} flex={1}>
                      <MetricCard label="FCP" value={checks.pagespeed.mobile.metrics.fcp?.displayValue} score={checks.pagespeed.mobile.metrics.fcp?.score} />
                      <MetricCard label="LCP" value={checks.pagespeed.mobile.metrics.lcp?.displayValue} score={checks.pagespeed.mobile.metrics.lcp?.score} />
                      <MetricCard label="TBT" value={checks.pagespeed.mobile.metrics.tbt?.displayValue} score={checks.pagespeed.mobile.metrics.tbt?.score} />
                      <MetricCard label="CLS" value={checks.pagespeed.mobile.metrics.cls?.displayValue} score={checks.pagespeed.mobile.metrics.cls?.score} />
                      <MetricCard label="SI" value={checks.pagespeed.mobile.metrics.speedIndex?.displayValue} score={checks.pagespeed.mobile.metrics.speedIndex?.score} />
                    </SimpleGrid>
                  )}
                </HStack>
              </SimpleGrid>
            </VStack>
          </Box>
        </Box>
      )}

      {/* SEO Table */}
      <CategoryTable
        title="SEO ESSENTIALS"
        icon="🔍"
        colorScheme="purple"
        checks={seoChecks}
      />

      {/* Tracking Table */}
      <CategoryTable
        title="TRACKING & ANALYTICS"
        icon="📊"
        colorScheme="pink"
        checks={trackingChecks}
      />

      {/* Integrations Table */}
      <CategoryTable
        title="INTEGRATIONS"
        icon="🔌"
        colorScheme="blue"
        checks={integrationChecks}
      />
    </VStack>
  );
}

export default ChecklistResults;
